import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { UsersMutations } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, NakedButton, Text, TextField, useAlert, useForm } from '@frontend/design-system';
import { MFABackButton } from './mfa-back-button';
import { MFAButtonRow } from './mfa-button-row';
import { useMFAContext } from './mfa-context';
import { MFA_MODAL_CONTACT_ADDED } from './pendo-tracking';

const styles = {
  field: css`
    margin: ${theme.spacing(3)} 0;
  `,
  linkButton: css`
    color: ${theme.colors.primary50};
    font-weight: ${theme.font.weight.bold};
    font-size: ${theme.font.size.large};
  `,
};

type VerifyMFAAddressProps = {
  onVerifySuccess?: () => Promise<void>;
};

export function VerifyMFAAddress({ onVerifySuccess }: VerifyMFAAddressProps) {
  const { t } = useTranslation('account');
  const alert = useAlert();
  const newVerificationMethod = useMFAContext((ctx) => ctx.newVerificationMethod);
  const nextStep = useMFAContext((ctx) => ctx.nextStep);

  const { mutateAsync: validateContact, isLoading } = UsersMutations.useValidateContact();
  const { mutateAsync: resendContactValidation, isLoading: isResendingCode } =
    UsersMutations.useResendContactValidation();

  const verify = async (code: string) => {
    try {
      await validateContact({ code, contactId: newVerificationMethod.contactId });
      // this is used in gatekeeper as well which does not have pendo, so we check if it exists
      if ((globalThis as any).pendo) {
        pendo.track(MFA_MODAL_CONTACT_ADDED, {
          visitorId: getUser()?.userID || '',
          type: newVerificationMethod.method || '',
        });
      }
      await onVerifySuccess?.();
      nextStep();
    } catch (error: any) {
      if (error?.status !== 403) {
        // just a breadcrumb for now because the backend needs to be updated to return better errors
        sentry.log({
          category: 'auth',
          message: 'Unexpected error received during MFA verification',
          data: { error },
        });
      }
      alert.error(
        t(
          'The verification code you entered is incorrect. Please check the code and try again. If you continue to experience issues, you can request a new verification code.'
        )
      );
    }
  };

  const { getFieldProps, formProps, isComplete } = useForm({
    fields: {
      code: {
        type: 'text',
        required: true,
        maxLength: 6,
      },
    },
    onSubmit(values) {
      if (!values.code) {
        return;
      }
      verify(values.code);
    },
  });
  const formRef = formProps.ref;
  const codeVal = getFieldProps('code').value;

  const autoSubmitForm = useCallback(
    (fieldVal: string) => {
      if (fieldVal.length >= 6) {
        // if the code is 6 digits long, we can submit the form automatically
        // submitting a form using formRef.current.submit() bypasses the React onSubmit handler, so this is the way
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    },
    [formRef]
  );

  // if the code is 6 digits long, we can submit the form automatically
  useEffect(() => {
    if (codeVal.length >= 6) {
      autoSubmitForm(codeVal);
    }
  }, [autoSubmitForm, codeVal]);

  const resendCode = useCallback(async () => {
    try {
      await resendContactValidation({ contactId: newVerificationMethod.contactId });
    } catch (error) {
      alert.error('Failed to send validation code.');
    }
  }, [alert, newVerificationMethod.contactId, resendContactValidation]);

  const formattedAddress = useMemo(() => {
    if (newVerificationMethod.method === 'sms') {
      return formatPhoneNumber(newVerificationMethod?.address);
    }
    return newVerificationMethod?.address;
  }, [newVerificationMethod]);

  return (
    <>
      <ContentLoader show={isLoading || isResendingCode} />
      <form {...formProps}>
        <Text>{t('Enter the code sent to {{address}} to complete setup.', { address: formattedAddress })}</Text>
        <div css={styles.field}>
          <TextField {...getFieldProps('code')} label={t('Verification Code')} autoFocus maxLength={6} />
        </div>
        <MFAButtonRow>
          <MFABackButton />
          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(2)};
            `}
          >
            <NakedButton css={styles.linkButton} onClick={resendCode}>
              {t('Resend')}
            </NakedButton>
            <Button type='submit' size='large' disabled={!isComplete}>
              {t('Verify')}
            </Button>
          </div>
        </MFAButtonRow>
      </form>
    </>
  );
}
